import React from 'react'
import { EnumFormFieldType, FormField, FormSection, OrderFormSchema } from '@cbgms/api'

// useHardcodedFieldTransforms applies default transformations to the schema
// for license-plates we apply custom logic on top of the orderform to apply styling and special cases.
// for 'dynamic' fields, we apply a transformation to add fields based on quantity.
export const useHardcodedFieldTransforms = (schema: OrderFormSchema | undefined): OrderFormSchema =>
  React.useMemo(() => {
    const baseSchema = { Form: !schema ? [] : schema.Form }
    let result = expandDynamicQuantityFields(baseSchema)
    result = handleHardcodedLicensePlateForm(result)

    return result
  }, [schema])

const expandDynamicQuantityFields = (schema: OrderFormSchema) => {
  // add 3 additional fields for each quantity
  const dynamicValues: { [key: string]: number } = { quantity: 3 }
  const transformPlaceholders = (field: FormField, indexKey: number) => {
    // replace '[name]' with indeKey
    const replace = (text: string) => text.replace(`[${field.Dynamic?.ID}]`, indexKey.toString())
    return {
      ...field,
      ID: replace(field.ID),
      Placeholder: replace(field.Placeholder)
    }
  }

  return {
    Form: schema.Form.map(section => ({
      SectionName: section.SectionName,
      Fields: section.Fields.reduce((acc, field: FormField) => {
        if (!field.Dynamic?.ID) {
          acc.push(field)
          return acc
        }
        const loopLength = dynamicValues[field.Dynamic.ID] || 0
        for (let index = 0; index < loopLength; index++) {
          acc.push(transformPlaceholders(field, index))
        }
        return acc
      }, new Array())
    }))
  }
}

const handleHardcodedLicensePlateForm = (schema: OrderFormSchema) => {
  let result = schema.Form.reduce<FormSection[]>((acc, formSection) => {
    if (!isEqual(formSection.SectionName, 'Kenteken')) {
      acc.push(formSection)
      return acc
    }
    acc.push({
      SectionName: 'Kenteken',
      Fields: sortFields(formSection.Fields.filter(f => isEqual(f.ID, 'quantity') || isEqual(f.ID, 'licensePlate')))
    })
    acc.push({
      SectionName: 'Identificatie',
      Fields: formSection.Fields.filter(f => !(isEqual(f.ID, 'quantity') || isEqual(f.ID, 'licensePlate')))
    })
    return acc
  }, [])

  // transform field types, some selects should be a button group
  result = result.map(f => {
    return {
      ...f,
      Fields: f.Fields.map(field => {
        const result = { ...field }
        if (
          isEqual(field.ID, 'selectedReasonForIssue') &&
          field.Type === EnumFormFieldType.Select &&
          field.Description === 'Reden van afgifte'
        ) {
          result.Type = EnumFormFieldType.SelectButtonGroup
          result.Description = '' // clear the description. since this button group is self descriptive
        }
        return result
      })
    }
  })

  return { Form: sortSections(result) }
}

const isEqual = (a: string, b: string) => {
  if (!a && !b) {
    return true
  }
  return (a || '').localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
}

const sortSections = (result: FormSection[]) => {
  const sortOrders: { [key: string]: number } = { Kenteken: 1, Optioneel: 2, Identificatie: 3, 'Reden van afgifte': 4 }
  return result.sort((a, b) => {
    if (sortOrders[a.SectionName] && sortOrders[b.SectionName]) {
      return sortOrders[a.SectionName] - sortOrders[b.SectionName]
    }
    return 0
  })
}

const sortFields = (result: FormField[]) => {
  const sortOrders: { [key: string]: number } = { licensePlate: 1, quantity: 2 }
  return result.sort((a, b) => {
    if (sortOrders[a.ID] && sortOrders[b.ID]) {
      return sortOrders[a.ID] - sortOrders[b.ID]
    }
    return 0 // unspecified fields should return as is.
  })
}
